import {
  deleteApiUserManagementV1ToolAccessUsername,
  getApiUserManagementV1ToolAccess,
  getApiUserManagementV1ToolAccessCandidates,
  postApiUserManagementV1ToolAccessUsernameRole
} from '@/libs/api-user-mgt/umOpenApiGaps/toolAccessNotInOpenApiClient';

import type { AssignToolRoleInput, ToolAccessUser } from '@/userManagement/domain/entities/ToolAccessUser';
import type { ToolAccessGrantCandidates } from '@/userManagement/domain/entities/ToolAccessGrantCandidate';

type WireRecord = Record<string, unknown>;

function readString(o: WireRecord, camel: string, pascal: string): string {
  const v = o[camel] ?? o[pascal];
  return typeof v === 'string' ? v : '';
}

function readNullableIso(o: WireRecord, camel: string, pascal: string): string | null {
  const v = o[camel] ?? o[pascal];
  if (v == null) return null;
  return String(v);
}

function mapToolAccessUser(raw: unknown): ToolAccessUser | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as WireRecord;
  const username = readString(o, 'username', 'Username');
  if (username === '') return null;
  return {
    username,
    displayName: readString(o, 'displayName', 'DisplayName') || username,
    roleName: readString(o, 'roleName', 'RoleName'),
    grantedAt: readString(o, 'grantedAt', 'GrantedAt'),
    lastToolLoginOn: readNullableIso(o, 'lastToolLoginOn', 'LastToolLoginOn'),
    isSystemAdmin: readBool(o, 'isSystemAdmin', 'IsSystemAdmin', false),
    allDatabases: readBool(o, 'allDatabases', 'AllDatabases', true),
    databaseNames: readStringList(o, 'databaseNames', 'DatabaseNames')
  };
}

function readStringList(o: WireRecord, camel: string, pascal: string): string[] {
  const v = o[camel] ?? o[pascal];
  return Array.isArray(v) ? v.map((item) => String(item)).filter((item) => item.trim() !== '') : [];
}

function readBool(o: WireRecord, camel: string, pascal: string, defaultValue = false): boolean {
  const v = o[camel] ?? o[pascal];
  return typeof v === 'boolean' ? v : defaultValue;
}

function readNullableString(o: WireRecord, camel: string, pascal: string): string | null {
  const v = o[camel] ?? o[pascal];
  if (v == null) return null;
  return String(v);
}

function mapGrantCandidate(raw: unknown) {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as WireRecord;
  const username = readString(o, 'username', 'Username');
  if (username === '') return null;
  return {
    username,
    displayName: readString(o, 'displayName', 'DisplayName') || username,
    isEligible: readBool(o, 'isEligible', 'IsEligible', true),
    ineligibilityReason: readNullableString(o, 'ineligibilityReason', 'IneligibilityReason')
  };
}

function mapGrantCandidates(raw: unknown): ToolAccessGrantCandidates {
  if (raw == null || typeof raw !== 'object') {
    return {
      candidates: [],
      activeDatabaseCount: 0,
      activeDatabaseNames: [],
      isComplete: true,
      warningMessage: null
    };
  }

  const o = raw as WireRecord;
  const rows = o.candidates ?? o.Candidates;
  const candidates = Array.isArray(rows) ? rows.map(mapGrantCandidate).filter((row) => row != null) : [];

  const activeDatabaseNamesRaw = o.activeDatabaseNames ?? o.ActiveDatabaseNames;
  const activeDatabaseNames = Array.isArray(activeDatabaseNamesRaw) ? activeDatabaseNamesRaw.map((name) => String(name)) : [];

  const activeDatabaseCountRaw = o.activeDatabaseCount ?? o.ActiveDatabaseCount;
  const activeDatabaseCount = typeof activeDatabaseCountRaw === 'number' ? activeDatabaseCountRaw : activeDatabaseNames.length;

  return {
    candidates,
    activeDatabaseCount,
    activeDatabaseNames,
    isComplete: readBool(o, 'isComplete', 'IsComplete', true),
    warningMessage: readNullableString(o, 'warningMessage', 'WarningMessage')
  };
}

export const userManagementToolAccessSegment = {
  listToolAccessUsers: async (): Promise<ToolAccessUser[]> => {
    const response = await getApiUserManagementV1ToolAccess();
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows.map(mapToolAccessUser).filter((row): row is ToolAccessUser => row != null);
  },

  listGrantAccessCandidates: async (): Promise<ToolAccessGrantCandidates> => {
    const response = await getApiUserManagementV1ToolAccessCandidates();
    return mapGrantCandidates(response.data);
  },

  assignToolRole: async (username: string, input: AssignToolRoleInput): Promise<void> => {
    await postApiUserManagementV1ToolAccessUsernameRole(username.trim(), {
      roleName: input.roleName,
      allDatabases: input.allDatabases,
      databaseNames: input.allDatabases ? [] : input.databaseNames
    });
  },

  revokeToolAccess: async (username: string): Promise<void> => {
    await deleteApiUserManagementV1ToolAccessUsername(username.trim());
  }
};
